/* Elwatanya — cart-driven UI: free-shipping progress, variant cards, line total.
   Reads Shopify state; never writes to the cart. */
(function () {
  'use strict';
  var FALLBACK = 280000;
  var CART_RE = /\/cart\/(add|change|update|clear)/;

  function money(cents) {
    var v = Math.max(0, cents) / 100;
    return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function setText(el, v) { if (el && el.textContent !== v) el.textContent = v; }
  function root() { return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; }

  /* ---------------- free shipping ----------------
     The amount remaining and the length of the bar are two renderings of ONE
     number: the live cart subtotal over the threshold read off the markup.
     progress = total / threshold, clamped to 0–100, never hardcoded. */
  var lastTotal = null;

  function paint(total) {
    var t = typeof total === 'number' ? total : parseInt(total, 10);
    if (!isFinite(t) || t < 0) {
      if (lastTotal === null) return; /* nothing trustworthy yet — keep the server state */
      t = lastTotal;
    }
    lastTotal = t;

    document.querySelectorAll('[data-elw-freeship]').forEach(function (box) {
      var th = parseInt(box.getAttribute('data-threshold'), 10) || FALLBACK;
      if (th <= 0) return;

      var remaining = Math.max(0, th - t);
      var pct = Math.max(0, Math.min(100, (t * 100) / th));
      var pctStr = pct.toFixed(1) + '%';

      box.classList.toggle('elw-freeship--met', remaining === 0);
      if (box.getAttribute('data-total') !== String(t)) box.setAttribute('data-total', t);
      setText(box.querySelector('[data-elw-freeship-remaining]'), money(remaining));

      /* Write the width every time, unconditionally. It was previously guarded
         on a data-progress attribute, which meant a stale attribute could leave
         the bar frozen while the amount beside it still updated — the exact
         symptom reported. The observer below watches childList only, so
         re-writing an identical inline style cannot feed back into it. */
      var fill = box.querySelector('[data-elw-freeship-fill]');
      if (fill) fill.style.width = pctStr;
      box.setAttribute('data-progress', pctStr);
      var track = box.querySelector('[data-elw-freeship-track]');
      if (track) track.setAttribute('aria-valuenow', String(Math.round(pct)));
    });
  }

  /* Paint straight from the server-rendered data-total on each box. Used before
     the first /cart.js read has landed, so a freshly rendered drawer is never
     left with an unpainted bar. */
  function paintFromMarkup() {
    var box = document.querySelector('[data-elw-freeship][data-total]');
    if (!box) return;
    var t = parseInt(box.getAttribute('data-total'), 10);
    if (isFinite(t)) paint(t);
  }

  var pending;
  function refresh() {
    clearTimeout(pending);
    pending = setTimeout(function () {
      fetch(root() + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) { if (c && typeof c.total_price === 'number') paint(c.total_price); })
        .catch(function () {});
    }, 60);
  }

  function watchCart() {
    var of = window.fetch;
    if (of && !of.elwWrapped) {
      var pf = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        return of.apply(this, arguments).then(function (res) {
          if (CART_RE.test(url)) refresh();
          return res;
        });
      };
      pf.elwWrapped = true;
      window.fetch = pf;
    }
    var oo = XMLHttpRequest.prototype.open;
    if (oo && !oo.elwWrapped) {
      var po = function (m, u) { this.__elwUrl = u; return oo.apply(this, arguments); };
      po.elwWrapped = true;
      XMLHttpRequest.prototype.open = po;
    }
    var os = XMLHttpRequest.prototype.send;
    if (os && !os.elwWrapped) {
      var ps = function () {
        var self = this;
        self.addEventListener('load', function () {
          if (CART_RE.test(self.__elwUrl || '')) refresh();
        });
        return os.apply(this, arguments);
      };
      ps.elwWrapped = true;
      XMLHttpRequest.prototype.send = ps;
    }
    bindPubSub();
  }

  /* Dawn publishes a cart total on some updates. Use it directly when it is
     there; otherwise fall back to reading /cart.js.

     Both `subscribe` and `PUB_SUB_EVENTS` are top-level `const`/`function`
     declarations in constants.js and pubsub.js, so PUB_SUB_EVENTS is a lexical
     global and never appears on `window`. This script is also deferred ahead of
     those two in theme.liquid, so it runs first — hence the typeof lookup and
     the retry on load. */
  var bound = false;
  function bindPubSub() {
    if (bound) return true;
    var ev = null, sub = null;
    try { if (typeof PUB_SUB_EVENTS !== 'undefined') ev = PUB_SUB_EVENTS; } catch (e) {}
    if (!ev) ev = window.PUB_SUB_EVENTS;
    try { if (typeof subscribe === 'function') sub = subscribe; } catch (e) {}
    if (!sub && typeof window.subscribe === 'function') sub = window.subscribe;
    if (!ev || !ev.cartUpdate || typeof sub !== 'function') return false;
    try {
      sub(ev.cartUpdate, function (evt) {
        var t = evt && evt.cartData && typeof evt.cartData.total_price === 'number'
          ? evt.cartData.total_price : null;
        if (t !== null) paint(t); else refresh();
      });
      /* Dawn publishes this once it has resolved and rendered the variant. It
         is the only trustworthy "the picker is settled now" signal we get. */
      if (ev.variantChange) sub(ev.variantChange, function () { onVariantSettled(); });
      bound = true;
    } catch (e) {}
    return bound;
  }

  /* ---------------- variant cards ---------------- */
  /* These cards are a facade; Dawn's native picker keeps owning variant state.
     The trap is that Dawn does not apply a change immediately. It debounces the
     option change (ON_CHANGE_DEBOUNCE_TIMER, 300ms), fetches the section, then
     swaps the whole buy box. Through that window the DOM passes states where
     the native inputs do not yet agree with what the customer just tapped.

     Repainting the cards from those inputs mid-window threw the selection back
     to the previous variant — and because the click handler used to bail out on
     `is-selected`, a card left holding that stale class then refused every
     retry. Paint drove the guard, so a bad repaint made the card permanently
     dead. The repaint ran on EVERY mutation anywhere in the document, the
     rotating announcement bar included, so it fired constantly.

     Now a tap records an intent. The intent outranks the DOM until Dawn
     confirms the variant, and is dropped the moment it is confirmed — or after
     INTENT_TTL if Dawn never answers. It must never outlive the truth: a card
     may not claim a variant the add-to-cart form is not carrying. */

  var INTENT_TTL = 4000;

  function nativeValue(vs) {
    var r = vs.querySelector('input[type="radio"]:checked');
    if (r) return r.value;
    var s = vs.querySelector('select');
    return s ? s.value : '';
  }

  function paintCards(wrap, value) {
    setText(wrap.querySelector('[data-elw-vcards-selected]'), value);
    wrap.querySelectorAll('[data-elw-vcard]').forEach(function (b) {
      var on = b.getAttribute('data-elw-value') === value;
      b.classList.toggle('is-selected', on);
      var v = on ? 'true' : 'false';
      if (b.getAttribute('aria-pressed') !== v) b.setAttribute('aria-pressed', v);
    });
  }

  function clearWant(wrap) {
    delete wrap.dataset.elwWant;
    delete wrap.dataset.elwWantAt;
  }

  function wantOf(wrap) {
    var w = wrap.dataset.elwWant;
    if (!w) return '';
    if (Date.now() - Number(wrap.dataset.elwWantAt || 0) > INTENT_TTL) { clearWant(wrap); return ''; }
    return w;
  }

  function mirror(wrap, vs) {
    var live = nativeValue(vs);
    var want = wantOf(wrap);
    if (want) {
      if (want !== live) { paintCards(wrap, want); return; }
      clearWant(wrap);
    }
    paintCards(wrap, live);
  }

  /* Drive the native control the way a real click would. For a <select>, only
     move selectedIndex and let Dawn's own updateSelectionMetadata shift the
     `selected` ATTRIBUTE — it looks the current one up by attribute and would
     throw if we cleared it first, and its selectedOptionValues getter reads
     `option[selected]`, not the property. */
  function applyToNative(vs, val) {
    var radios = vs.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].value === val) {
        radios[i].checked = true;
        radios[i].dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    var sel = vs.querySelector('select');
    if (!sel) return false;
    for (var j = 0; j < sel.options.length; j++) {
      if (sel.options[j].value === val) {
        sel.selectedIndex = j;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    return false;
  }

  function cardWraps(fn) {
    document.querySelectorAll('[data-elw-variant-cards]').forEach(function (wrap) {
      var info = wrap.closest('.product__info-container');
      var vs = (info || document).querySelector('variant-selects');
      if (vs) fn(wrap, vs);
    });
  }

  /* Delegated from the document, deliberately. Dawn replaces the whole buy box
     on every variant change, so per-button listeners die with the old markup
     and the replacement cards only work if something re-binds them. That
     re-bind used to depend on a debounced MutationObserver, and a debounce can
     be starved: the announcement bar mutates faster than the 120ms window, so
     clearTimeout kept resetting the timer and the re-bind NEVER ran. The cards
     rendered correctly and were completely dead — the tap did nothing at all.
     Delegation removes the dependency: freshly rendered cards work instantly
     because the listener never lived on them in the first place. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-elw-vcard]');
    if (!btn || btn.disabled) return;
    var wrap = btn.closest('[data-elw-variant-cards]');
    if (!wrap) return;
    var info = wrap.closest('.product__info-container');
    var vs = (info || document).querySelector('variant-selects');
    if (!vs) return;

    var val = btn.getAttribute('data-elw-value');
    /* Compared against the native input, never against is-selected: that class
       is paint, and paint must not decide what is clickable. */
    if (val === nativeValue(vs)) { mirror(wrap, vs); return; }
    wrap.dataset.elwWant = val;
    wrap.dataset.elwWantAt = String(Date.now());
    paintCards(wrap, val);              /* instant feedback, no wait on Dawn */
    applyToNative(vs, val);
  });

  function initCards() {
    cardWraps(function (wrap, vs) {
      /* Hide the native picker only once the cards can act, so a script failure
         leaves the stock picker usable instead of hiding it behind dead cards. */
      vs.classList.add('elw-native-hidden');
      mirror(wrap, vs);
    });
  }

  /* Dawn resolved a variant: the DOM is now the truth, so the intent retires. */
  function onVariantSettled() {
    requestAnimationFrame(function () {
      cardWraps(function (wrap, vs) {
        clearWant(wrap);
        paintCards(wrap, nativeValue(vs));
      });
    });
  }

  /* ---------------- running line total (spec ⑦) ---------------- */
  function variantPrice(info) {
    var s = info.querySelector('[data-selected-variant]');
    if (!s) return null;
    try {
      var v = JSON.parse(s.textContent);
      return v && typeof v.price === 'number' ? v.price : null;
    } catch (e) { return null; }
  }

  /* This total is price × quantity, which is right only while price × quantity
     is what the cart charges. On a product that carries a quantity ladder it is
     not: an approved tier total (10 bubble rolls = 8,500, backed by an automatic
     discount) is less than 10 × 900. The ladder owns "الإجمالي" on those pages —
     printing a second, contradicting total next to it is how a customer ends up
     reading 9,000 and being charged 8,500. */
  function initTotals() {
    document.querySelectorAll('.product__info-container').forEach(function (info) {
      var qty = info.querySelector('.quantity__input');
      if (!qty) return;
      if (info.querySelector('[data-elw-ladder]')) {
        var dup = info.querySelector('[data-elw-linetotal]');
        if (dup) dup.hidden = true;
        return;
      }
      var host = info.querySelector('[data-elw-linetotal]');
      if (!host) {
        var anchor = qty.closest('.product-form__quantity') || qty.parentElement;
        if (!anchor) return;
        host = document.createElement('div');
        host.className = 'elw-linetotal';
        host.setAttribute('data-elw-linetotal', '');
        host.innerHTML =
          '<span class="elw-linetotal__label">الإجمالي</span>' +
          '<span class="elw-linetotal__value elw-num" data-elw-linetotal-value></span>';
        anchor.appendChild(host);
      }
      if (qty.dataset.elwLt !== '1') {
        qty.dataset.elwLt = '1';
        qty.addEventListener('change', initTotals);
        qty.addEventListener('input', initTotals);
      }
      var price = variantPrice(info);
      var n = parseInt(qty.value, 10) || 1;
      if (price == null) { host.hidden = true; return; }
      host.hidden = false;
      setText(host.querySelector('[data-elw-linetotal-value]'), money(price * n) + ' ج');
    });
  }

  function init() { initCards(); initTotals(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { watchCart(); init(); refresh(); });
  } else {
    watchCart(); init(); refresh();
  }

  /* constants.js / pubsub.js are deferred after this file, so retry the
     subscription once every deferred script has run. */
  window.addEventListener('load', function () { bindPubSub(); });

  document.addEventListener('shopify:section:load', function () { bindPubSub(); init(); refresh(); });

  window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });

  /* The observer exists for cart markup the server re-renders. It used to run
     the variant-card sync too, on every mutation anywhere on the page — the
     rotating announcement bar alone fired it several times a second, and each
     run repainted the cards from a picker that might be mid-swap. Card work is
     now limited to mutations that actually added or removed buy-box markup. */
  function touchesBuyBox(records) {
    for (var i = 0; i < records.length; i++) {
      var nodes = [].concat([].slice.call(records[i].addedNodes), [].slice.call(records[i].removedNodes));
      for (var j = 0; j < nodes.length; j++) {
        var n = nodes[j];
        if (n.nodeType !== 1) continue;
        if (n.matches && (n.matches('variant-selects, [data-elw-variant-cards], .product__info-container') ||
            n.querySelector('variant-selects, [data-elw-variant-cards]'))) return true;
      }
    }
    return false;
  }

  /* A plain clearTimeout debounce can be starved: anything mutating faster than
     the window — the rotating announcement bar does, every 50ms — resets the
     timer forever and the callback never runs. That is what killed the variant
     cards, and it was silently costing the cart repaint too. So the flag is
     accumulated rather than overwritten, and MAX_WAIT guarantees the work runs
     however noisy the page is. */
  var DEBOUNCE = 120, MAX_WAIT = 500;
  var t, firstQueued = 0, cardsDirty = false;

  new MutationObserver(function (records) {
    cardsDirty = cardsDirty || touchesBuyBox(records);
    var now = Date.now();
    if (!firstQueued) firstQueued = now;
    if (now - firstQueued >= MAX_WAIT) { clearTimeout(t); flush(); return; }
    clearTimeout(t);
    t = setTimeout(flush, DEBOUNCE);
  }).observe(document.documentElement, { childList: true, subtree: true });

  function flush() {
    firstQueued = 0;
    if (cardsDirty) { cardsDirty = false; initCards(); }
    initTotals();
    /* A re-rendered drawer/cart brings fresh server markup. Re-assert from the
       newest total we know: the live one if we have it, otherwise whatever the
       server just wrote onto the element. */
    if (lastTotal !== null) paint(lastTotal); else paintFromMarkup();
  }
})();
